/** One video slot (default / weekday / job): upload with progress, preview, remove. */
import { useRef, useState } from "react";
import { Upload, Loader2, Video, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CHUNK = 4 * 1024 * 1024;

export default function SafetySlot({ slotKey, label, meta, onChange, canRemove = true, hint, library = [] }) {
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);
  const has = !!meta?.has_video;

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const { data: start } = await api.post("/safety/video/start", { name: file.name, mime: file.type || "video/mp4", size: file.size, slot: slotKey, project_name: label });
      for (let off = 0; off < file.size; off += CHUNK) {
        await api.put(`/safety/video/${start.upload_id}/chunk`, file.slice(off, off + CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
        setProgress(Math.min(99, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      const { data } = await api.post(`/safety/video/${start.upload_id}/complete`);
      onChange(data); setPreview(false);
      toast.success(`${label}: video uploaded (v${data.slots[slotKey]?.version})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setProgress(null); }
  };
  const remove = async () => {
    try { const { data } = await api.delete("/safety/video", { params: { slot: slotKey } }); onChange(data); toast.success(`${label}: slot cleared (video kept in library)`); }
    catch { toast.error("Could not remove"); }
  };
  const pickFromLibrary = async (videoId) => {
    try { const { data } = await api.post(`/safety/library/${videoId}/assign`, { slot: slotKey, project_name: label }); onChange(data); setPreview(false); toast.success(`${label}: video set from library`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  const tid = slotKey.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div className="rounded-lg border border-gray-200 p-3" data-testid={`safety-slot-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
          <div className="truncate text-[11px] text-gray-500" data-testid={`safety-slot-${tid}-meta`}>
            {has ? `${meta.video_name} · v${meta.version} · ${(meta.video_size / 1048576).toFixed(1)} MB · ${new Date(meta.uploaded_at).toLocaleDateString()} by ${meta.uploaded_by}` : (hint || "No video — the default video plays")}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`safety-slot-${tid}-file`} />
        <button onClick={() => fileRef.current?.click()} disabled={progress !== null} className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60" data-testid={`safety-slot-${tid}-upload`}>
          {progress !== null ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {progress}%</> : <><Upload className="h-3.5 w-3.5" /> {has ? "Replace" : "Upload"}</>}
        </button>
        {library?.length > 0 && (
          <select value="" onChange={(e) => e.target.value && pickFromLibrary(e.target.value)} className="max-w-[11rem] rounded-md border border-gray-300 bg-transparent px-2 py-1.5 text-xs text-gray-700 dark:text-white/80 dark:[&>option]:text-black" title="Re-use a past toolbox talk" data-testid={`safety-slot-${tid}-library`}>
            <option value="">From library…</option>
            {library.map((v) => <option key={v.video_id} value={v.video_id}>{v.title}</option>)}
          </select>
        )}
        {has && <button onClick={() => setPreview((p) => !p)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 dark:text-white/80" data-testid={`safety-slot-${tid}-preview`}><Video className="h-3.5 w-3.5" /> {preview ? "Hide" : "Preview"}</button>}
        {has && canRemove && <button onClick={remove} className="rounded-md border border-red-300 p-1.5 text-red-600" aria-label="Remove video" data-testid={`safety-slot-${tid}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
      {preview && has && (
        <video key={meta.version} controls playsInline preload="metadata" className="mt-3 aspect-video w-full max-w-md rounded-lg bg-black"
          src={`${process.env.REACT_APP_BACKEND_URL}/api/safety/video?slot=${encodeURIComponent(slotKey)}&v=${meta.version}`} data-testid={`safety-slot-${tid}-video`} />
      )}
    </div>
  );
}
